/**
 * DocumentRejectionModal - Reject document with re-request option
 *
 * Features:
 * - Rejection reason selection
 * - Optional .eml file generation for re-request
 * - Reminder schedule configuration
 */

import React, { useState } from 'react';
import { X, XCircle, Mail, Loader2, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { documentValidationAPI } from '../../api.js';
import { REJECTION_REASONS } from '../../config/complianceRules.js';

/**
 * DocumentRejectionModal
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - doc: Document object being rejected
 * - onRejected: (result) => void
 */
export default function DocumentRejectionModal({
  isOpen,
  onClose,
  doc,
  defaultRecipientEmail = '',
  defaultRecipientName = '',
  onRejected
}) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [generateEml, setGenerateEml] = useState(true);
  const [recipientEmail, setRecipientEmail] = useState(defaultRecipientEmail);
  const [recipientName, setRecipientName] = useState(defaultRecipientName);
  const [reminderSchedule, setReminderSchedule] = useState([3, 7, 14]);
  const [submitting, setSubmitting] = useState(false);
  const [, setEmlDownloaded] = useState(false);

  // Get final rejection reason
  const rejectionReason = selectedReason === 'other' ? customReason : selectedReason;

  // Handle submit
  const handleSubmit = async () => {
    if (!rejectionReason) {
      alert(t('documents.rejection.errors.reasonRequired'));
      return;
    }

    if (generateEml && !recipientEmail) {
      alert(t('documents.rejection.errors.recipientRequired'));
      return;
    }

    setSubmitting(true);
    try {
      // Reject the document
      const result = await documentValidationAPI.reject(doc.id, {
        rejection_reason: rejectionReason,
        notes,
        generate_eml: generateEml,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        reminder_schedule: reminderSchedule
      });

      if (result.ok) {
        // If EML requested, trigger download
        if (generateEml && recipientEmail) {
          const emlUrl = documentValidationAPI.getRejectionEmailUrl(
            doc.id,
            recipientEmail,
            recipientName
          );
          window.open(emlUrl, '_blank');
          setEmlDownloaded(true);
        }

        onRejected?.(result.data);
        onClose();
      } else {
        alert(result.error?.message || t('documents.rejection.errors.rejectFailed'));
      }
    } catch (err) {
      console.error('Rejection error:', err);
      alert(t('documents.rejection.errors.rejectFailedDetail', { message: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle reminder day
  const toggleReminderDay = (day) => {
    setReminderSchedule(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-red-50">
          <h2 className="text-lg font-semibold text-red-800 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            {t('documents.rejection.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Document info */}
          <div className="text-sm text-gray-600">
            {t('documents.rejection.documentLabel')} <span className="font-medium">{doc?.file_name}</span>
          </div>

          {/* Rejection reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('documents.rejection.reasonLabel')} <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {REJECTION_REASONS.map(reason => (
                <label key={reason.code} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="rejection_reason"
                    value={reason.code}
                    checked={selectedReason === reason.code}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">{t(`documents.rejection.reasons.${reason.code}`, { defaultValue: reason.label })}</span>
                </label>
              ))}
            </div>

            {/* Custom reason input */}
            {selectedReason === 'other' && (
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={t('documents.rejection.customReasonPlaceholder')}
                className="mt-2 w-full px-3 py-2 border rounded text-sm"
                rows={2}
              />
            )}
          </div>

          {/* Additional notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('documents.rejection.notesLabel')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('documents.rejection.notesPlaceholder')}
              className="w-full px-3 py-2 border rounded text-sm"
              rows={2}
            />
          </div>

          {/* Generate EML option */}
          <div className="border-t pt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={generateEml}
                onChange={(e) => setGenerateEml(e.target.checked)}
                className="text-blue-600 focus:ring-blue-500 rounded"
              />
              <span className="text-sm text-gray-700 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {t('documents.rejection.generateEml')}
              </span>
            </label>

            {generateEml && (
              <div className="mt-3 ml-6 space-y-3 p-3 bg-gray-50 rounded">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('documents.rejection.recipient')}
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('documents.rejection.recipientName')}
                  </label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder={t('documents.rejection.recipientNamePlaceholder')}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('documents.rejection.reminders')}
                  </label>
                  <div className="flex gap-2">
                    {[3, 7, 14, 21].map(day => (
                      <label
                        key={day}
                        className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer text-xs ${
                          reminderSchedule.includes(day)
                            ? 'bg-blue-100 border-blue-300 text-blue-700'
                            : 'bg-white border-gray-200 text-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={reminderSchedule.includes(day)}
                          onChange={() => toggleReminderDay(day)}
                          className="sr-only"
                        />
                        {t('documents.rejection.reminderDay', { days: day })}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
          >
            {t('common.actions.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !rejectionReason}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : generateEml ? (
              <Download className="w-4 h-4" />
            ) : (
              <XCircle className="w-4 h-4" />
            )}
            {generateEml ? t('documents.rejection.submitWithEml') : t('documents.rejection.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
